import { useQuery } from '@tanstack/react-query'
import api from '../api'

// Query keys
export const queryKeys = {
  admin: ['admin'] as const,
  users: ['users'] as const,
  user: ['user'] as const,
  notifications: ['notifications'] as const,
  stats: ['stats'] as const,
  contentCreatorRequests: ['contentCreatorRequests'] as const,
  publishRequests: ['publishRequests'] as const,
  contentDetail: ['contentDetail'] as const,
  frontroomVideos: ['frontroomVideos'] as const,
  frontroomVideo: ['frontroomVideo'] as const,
  contentCreatorArticles: ['contentCreatorArticles'] as const,
  contentCreatorArticle: ['contentCreatorArticle'] as const,
  contentCreatorPodcasts: ['contentCreatorPodcasts'] as const,
  contentCreatorPodcast: ['contentCreatorPodcast'] as const,
}

// Types for user query parameters
export interface UsersQueryParams {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  createMode?: boolean
}

// Types for content creator requests query parameters
export interface ContentCreatorRequestsQueryParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  email?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

// Types for publish requests query parameters
export interface PublishRequestsQueryParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  contentType?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

export interface FrontroomVideosQueryParams {
  page?: number
  limit?: number
  search?: string
  sortBy?: 'order' | 'createdAt' | 'updatedAt'
  sortOrder?: 'ASC' | 'DESC'
  category?: string
  isPublished?: boolean
}

export interface ContentCreatorArticlesQueryParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  category?: string
  creatorId?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  contentType?: string
}

export interface ContentCreatorArticleCreator {
  _id?: string
  email?: string
  userName?: string
  firstName?: string
  lastName?: string
  profilePhoto?: string
}

export interface ContentCreatorArticle {
  _id?: string
  id?: string
  postId?: string
  title?: string
  shortDescription?: string
  description?: string
  category?: string
  coverImage?: string
  creatorId?: string
  status?: string
  reads?: number
  likes?: number
  comments?: number
  tags?: string[]
  isPublished?: boolean
  readingTime?: number
  createdAt?: string
  updatedAt?: string
  adminNotes?: string
  publishedAt?: string
  creator?: ContentCreatorArticleCreator
  contentType?: string
  [key: string]: unknown
}

export interface ContentCreatorPodcastsQueryParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  category?: string
  creatorId?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  contentType?: string
}

export interface ContentCreatorPodcastCreator {
  _id?: string
  email?: string
  userName?: string
  firstName?: string
  lastName?: string
  profilePhoto?: string
}

export interface ContentCreatorPodcast {
  _id?: string
  id?: string
  podcastId?: string
  postId?: string
  title?: string
  description?: string
  category?: string
  thumbnail?: string
  videoUrl?: string
  duration?: number
  tags?: string[]
  status?: string
  creatorId?: string
  creator?: ContentCreatorPodcastCreator
  contentType?: string
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

// Admin profile query
export const useAdminProfile = () => {
  return useQuery({
    queryKey: queryKeys.admin,
    queryFn: async () => {
      const response = await api.get('/profile')
      return response.data
    },
    enabled: false, // Only fetch when explicitly called
  })
}

// Users query with parameters
export const useUsers = (params: UsersQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    createMode,
  } = params

  return useQuery({
    queryKey: [...queryKeys.users, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      
      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)
      if (createMode !== undefined) queryParams.append('createMode', createMode.toString())

      const response = await api.get(`/users?${queryParams.toString()}`)
      return response.data
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      // Retry once for other errors
      return failureCount < 1
    },
  })
}

export const useFrontroomVideos = (params: FrontroomVideosQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    category = '',
    isPublished,
  } = params

  return useQuery({
    queryKey: [...queryKeys.frontroomVideos, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)
      if (category) queryParams.append('category', category)
      if (isPublished !== undefined) queryParams.append('isPublished', isPublished.toString())

      const response = await api.get(`/frontroom/videos?${queryParams.toString()}`)
      return response.data
    },
  })
}

export const useContentCreatorArticles = (params: ContentCreatorArticlesQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    status = '',
    category = '',
    creatorId = '',
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    contentType = 'article',
  } = params

  return useQuery({
    queryKey: [...queryKeys.contentCreatorArticles, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (status) queryParams.append('status', status)
      if (category) queryParams.append('category', category)
      if (creatorId) queryParams.append('creatorId', creatorId)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)
      if (contentType) queryParams.append('contentType', contentType)

      const response = await api.get(`/content-creator/articles?${queryParams.toString()}`)
      return response.data
    },
  })
}

export const useContentCreatorArticle = (articleId?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...queryKeys.contentCreatorArticle, articleId],
    queryFn: async () => {
      const response = await api.get(`/content-creator/articles/${articleId}`)
      return response.data
    },
    enabled: Boolean(enabled && articleId),
  })
}

export const useContentCreatorPodcasts = (params: ContentCreatorPodcastsQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    status = '',
    category = '',
    creatorId = '',
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    contentType = 'podcast',
  } = params

  return useQuery({
    queryKey: [...queryKeys.contentCreatorPodcasts, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (status) queryParams.append('status', status)
      if (category) queryParams.append('category', category)
      if (creatorId) queryParams.append('creatorId', creatorId)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)
      if (contentType) queryParams.append('contentType', contentType)

      const response = await api.get(`/content-creator/podcasts?${queryParams.toString()}`)
      return response.data
    },
  })
}

export const useContentCreatorPodcast = (podcastId?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...queryKeys.contentCreatorPodcast, podcastId],
    queryFn: async () => {
      const response = await api.get(`/content-creator/podcasts/${podcastId}`)
      return response.data
    },
    enabled: Boolean(enabled && podcastId),
  })
}

export const useFrontroomVideoDetail = (videoId?: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: [...queryKeys.frontroomVideo, videoId],
    queryFn: async () => {
      const response = await api.get(`/frontroom/videos/${videoId}`)
      const payload = response.data

      if (!payload) return null
      if (payload.video) return payload.video
      if (payload.data?.video) return payload.data.video
      if (Array.isArray(payload.data)) return payload.data[0]
      if (payload.data) return payload.data
      return payload
    },
    enabled: Boolean(enabled && videoId),
  })
}

// Notifications query
export const useNotifications = () => {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: async () => {
      const response = await api.get('/notifications')
      return response.data
    },
  })
}

// User detail query
export const useUserDetail = (userId: string) => {
  return useQuery({
    queryKey: [...queryKeys.user, userId],
    queryFn: async () => {
      const response = await api.get(`/users/${userId}`)
      return response.data
    },
    enabled: !!userId,
  })
}

// Dashboard stats query
export const useDashboardStats = () => {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: async () => {
      const response = await api.get('/stats')
      return response.data
    },
  })
} 

// Content creator requests query
export const useContentCreatorRequests = (params: ContentCreatorRequestsQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    status = 'pending',
    email = '',
    sortBy = '',
    sortOrder = 'DESC',
  } = params

  return useQuery({
    queryKey: [...queryKeys.contentCreatorRequests, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      
      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (status) queryParams.append('status', status)
      if (email) queryParams.append('email', email)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)

      const response = await api.get(`/content-creator-requests?${queryParams.toString()}`)
      return response.data
    },
  })
} 

// Publish requests query
export const usePublishRequests = (params: PublishRequestsQueryParams = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    status = '',
    contentType = '',
    sortBy = 'createdAt',
    sortOrder = 'DESC',
  } = params

  return useQuery({
    queryKey: [...queryKeys.publishRequests, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (page) queryParams.append('page', page.toString())
      if (limit) queryParams.append('limit', limit.toString())
      if (search) queryParams.append('search', search)
      if (status) queryParams.append('status', status)
      if (contentType) queryParams.append('contentType', contentType)
      if (sortBy) queryParams.append('sortBy', sortBy)
      if (sortOrder) queryParams.append('sortOrder', sortOrder)

      const response = await api.get(`/content?${queryParams.toString()}`)
      return response.data
    },
  })
}

// Content detail query for a specific item
export const useContentDetail = (
  contentType?: string,
  contentId?: string,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: [...queryKeys.contentDetail, contentType, contentId],
    queryFn: async () => {
      const response = await api.get(`/content/${contentType}/${contentId}`)
      return response.data
    },
    enabled: Boolean(enabled && contentType && contentId),
  })
}